import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, CheckCircle, MapPin } from 'lucide-react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      placed: <Package className="h-6 w-6" />,
      confirmed: <Package className="h-6 w-6" />,
      in_transit: <Truck className="h-6 w-6" />,
      delivered: <CheckCircle className="h-6 w-6" />
    };
    return icons[status] || <Package className="h-6 w-6" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-slate-600 mb-4">Order not found</p>
            <Button onClick={() => navigate('/orders')}>Back to Orders</Button>
          </div>
        </div>
      </div>
    );
  }

  const defaultLocation = { lat: 19.0760, lng: 72.8777 };
  const currentLocation = order.current_location || defaultLocation;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/orders')}
            className="mb-6"
            data-testid="back-to-orders"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>

          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-primary mb-2 uppercase tracking-tight" data-testid="tracking-heading">
            Order Tracking
          </h1>
          <p className="text-slate-600 mb-8">
            Order ID: <span className="font-mono font-medium">{order.id.slice(0, 12)}</span>
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tracking Timeline */}
            <div className="lg:col-span-2 space-y-6">
              {/* Map */}
              <div className="bg-white rounded-sm shadow-sm overflow-hidden" data-testid="delivery-map">
                <div className="h-96">
                  {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <Map
                        defaultCenter={currentLocation}
                        defaultZoom={13}
                        mapId="balaji-delivery-map"
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        zoomControl={true}
                      >
                        <AdvancedMarker position={currentLocation}>
                          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center shadow-lg">
                            <Truck className="h-6 w-6 text-white" />
                          </div>
                        </AdvancedMarker>
                      </Map>
                    </APIProvider>
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                      <div className="text-center p-8">
                        <MapPin className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-600">Map view available after delivery starts</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tracking History */}
              <div className="bg-white rounded-sm shadow-sm p-6" data-testid="tracking-history">
                <h2 className="font-heading text-2xl font-bold text-primary mb-6 uppercase">Tracking History</h2>
                <div className="space-y-4">
                  {order.tracking_history?.map((event, index) => (
                    <div key={index} className="flex gap-4" data-testid={`tracking-event-${index}`}>
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                          {getStatusIcon(event.status)}
                        </div>
                        {index < order.tracking_history.length - 1 && (
                          <div className="w-0.5 h-full bg-slate-200 mt-2" />
                        )}
                      </div>
                      <div className="flex-1 pb-8">
                        <p className="font-medium text-primary uppercase">{event.status.replace('_', ' ')}</p>
                        <p className="text-sm text-slate-600 mt-1">{event.message}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )) || (
                    <p className="text-slate-600">No tracking updates yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div>
              <div className="bg-white rounded-sm shadow-sm p-6 space-y-6 sticky top-24" data-testid="order-details">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-primary mb-4 uppercase">Order Details</h2>
                  <div className="space-y-3">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-slate-600">{item.name} x{item.quantity}</span>
                        <span className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t border-slate-200 pt-3">
                      <div className="flex justify-between text-lg font-bold text-primary">
                        <span>Total</span>
                        <span>₹{order.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-heading text-lg font-bold text-primary mb-3 uppercase">Delivery Address</h3>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p className="font-medium">{order.delivery_info.name}</p>
                    <p>{order.delivery_info.phone}</p>
                    <p>{order.delivery_info.address}</p>
                    <p>{order.delivery_info.city}, {order.delivery_info.state} - {order.delivery_info.pincode}</p>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-heading text-lg font-bold text-primary mb-3 uppercase">Payment</h3>
                  <div className="text-sm text-slate-600">
                    <p>Method: <span className="font-medium capitalize">{order.payment_method}</span></p>
                    <p>Status: <span className="font-medium capitalize">{order.payment_status}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderTracking;